import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, CalendarPlus, ClipboardList, Activity, LogOut,
  TrendingUp, Users, DollarSign, Clock, CheckCircle, XCircle, Link2, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const getAffiliateAxios = () => {
  const token = localStorage.getItem('affiliate_token');
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
};

// ============================================
// LAYOUT
// ============================================
const AffiliateLayout = ({ children, affiliate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAffiliateDomain = window.location.hostname.includes('activities.sefertunisia') || window.location.hostname.includes('activities.sefer');
  const prefix = isAffiliateDomain ? '' : '/affiliate';

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_data');
    toast.success('Logged out');
    navigate(isAffiliateDomain ? '/login' : '/affiliate/login');
  };

  const menuItems = [
    { path: `${prefix}/dashboard`, label: 'Dashboard', icon: LayoutDashboard },
    { path: `${prefix}/activities`, label: 'Activities', icon: Activity },
    { path: `${prefix}/bookings`, label: 'Bookings', icon: ClipboardList },
    { path: `${prefix}/new-booking`, label: 'New Booking', icon: CalendarPlus },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button className="md:hidden mr-3 p-2 rounded-lg hover:bg-gray-100" onClick={() => setMobileOpen(!mobileOpen)}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
              <span className="text-xl font-bold text-emerald-600">Sefer Tunisia</span>
              <span className="text-sm text-gray-500 ml-2 hidden sm:inline">Affiliate Portal</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 hidden sm:inline">{affiliate?.company_name}</span>
              <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full">{affiliate?.type}</span>
              <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
      <div className="flex">
        <aside className={`${mobileOpen ? 'block' : 'hidden'} md:block w-56 bg-white border-r border-gray-200 min-h-[calc(100vh-4rem)] fixed md:sticky top-16 z-30`}>
          <nav className="p-4 space-y-1">
            {menuItems.map(item => (
              <button key={item.path} onClick={() => { navigate(item.path); setMobileOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition
                  ${isActive(item.path) ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="flex-1 p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

// ============================================
// STATS DASHBOARD
// ============================================
const StatsDashboard = ({ affiliate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAffiliateAxios().get(`${API}/affiliate/stats`);
        setStats(res.data);
      } catch { toast.error('Failed to load stats'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const publicUrl = `${window.location.origin}/book/${affiliate?.slug || ''}`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied!'); };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!stats) return <div className="text-center py-12 text-gray-400">No data</div>;

  const cards = [
    { label: 'Total Bookings', value: stats.total_bookings, icon: ClipboardList, color: 'blue' },
    { label: 'Total Guests', value: stats.total_guests, icon: Users, color: 'purple' },
    { label: 'Total Sales', value: `${stats.total_sales.toFixed(2)} EUR`, icon: DollarSign, color: 'emerald' },
    { label: 'Commission', value: `${stats.total_commission.toFixed(2)} EUR`, icon: TrendingUp, color: 'amber' },
    { label: 'Pending', value: stats.pending_count, icon: Clock, color: 'orange' },
    { label: 'Confirmed', value: stats.confirmed_count, icon: CheckCircle, color: 'green' },
  ];

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard-stats">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <Link2 className="w-4 h-4 text-emerald-600" />
          <span className="text-sm text-emerald-700 truncate max-w-xs">{publicUrl}</span>
          <button onClick={copyLink} className="p-1 hover:bg-emerald-100 rounded" data-testid="copy-public-link">
            <Copy className="w-4 h-4 text-emerald-600" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <c.icon className={`w-4 h-4 text-${c.color}-600`} />
              <span className="text-xs text-gray-500">{c.label}</span>
            </div>
            <div className="text-xl font-bold text-gray-900">{c.value}</div>
          </div>
        ))}
      </div>

      {stats.upcoming_bookings?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Upcoming Bookings</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="pb-2">Date</th><th className="pb-2">Time</th><th className="pb-2">Activity</th>
                <th className="pb-2">Guest</th><th className="pb-2">Guests</th><th className="pb-2">Status</th>
              </tr></thead>
              <tbody>
                {stats.upcoming_bookings.map(b => (
                  <tr key={b.id} className="border-b last:border-0">
                    <td className="py-2">{b.booking_date}</td>
                    <td className="py-2">{b.booking_time || '-'}</td>
                    <td className="py-2 font-medium">{b.activity_name}</td>
                    <td className="py-2">{b.guest_name}</td>
                    <td className="py-2">{b.num_guests}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                        ${b.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {Object.keys(stats.activity_breakdown || {}).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Activity Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(stats.activity_breakdown).map(([name, data]) => (
              <div key={name} className="bg-gray-50 rounded-lg p-3">
                <div className="font-medium text-sm">{name}</div>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  <span>{data.count} bookings</span>
                  <span>{data.guests} guests</span>
                  <span className="text-emerald-600 font-medium">{data.revenue.toFixed(2)} EUR</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ACTIVITIES LIST
// ============================================
const ActivitiesList = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAffiliateAxios().get(`${API}/affiliate/activities`);
        setActivities(res.data);
      } catch { toast.error('Failed to load activities'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="affiliate-activities">
      <h2 className="text-2xl font-bold text-gray-900">Available Activities</h2>
      {activities.length === 0 ? (
        <p className="text-gray-400 py-8 text-center">No activities available yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activities.map(a => (
            <div key={a.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition" data-testid={`activity-card-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{a.name}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                  ${a.type === 'trip' ? 'bg-blue-100 text-blue-700' : a.type === 'service' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {a.type}
                </span>
              </div>
              {a.description && <p className="text-sm text-gray-500 mb-3">{a.description}</p>}
              <div className="space-y-2 border-t pt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Your price (B2B)</span>
                  <span className="font-semibold text-emerald-600">{a.b2b_price} {a.b2b_currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Recommended B2C</span>
                  <span className="font-medium text-gray-700">{a.b2c_price} {a.b2c_currency}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// BOOKINGS LIST
// ============================================
const BookingsList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await getAffiliateAxios().get(`${API}/affiliate/bookings${params}`);
      setBookings(res.data);
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await getAffiliateAxios().put(`${API}/affiliate/bookings/${id}`, { status });
      toast.success(`Booking ${status}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="affiliate-bookings">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Bookings</h2>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="booking-status-filter">
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {bookings.length === 0 ? (
        <p className="text-gray-400 py-8 text-center">No bookings yet.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 text-left text-gray-500 text-xs uppercase">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Activity</th><th className="px-4 py-3">Guest</th>
                <th className="px-4 py-3">Phone</th><th className="px-4 py-3">Guests</th><th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} className="border-t hover:bg-gray-50" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-3">{b.booking_date}{b.booking_time ? ` ${b.booking_time}` : ''}</td>
                    <td className="px-4 py-3 font-medium">{b.activity_name}</td>
                    <td className="px-4 py-3">{b.guest_name}</td>
                    <td className="px-4 py-3">{b.guest_phone}</td>
                    <td className="px-4 py-3">{b.num_guests}</td>
                    <td className="px-4 py-3">{b.selling_price} {b.selling_currency}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[b.status] || ''}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {b.status === 'pending' && (
                          <>
                            <button onClick={() => updateStatus(b.id, 'confirmed')}
                              className="p-1 text-green-600 hover:bg-green-50 rounded" title="Confirm">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => updateStatus(b.id, 'cancelled')}
                              className="p-1 text-red-600 hover:bg-red-50 rounded" title="Cancel">
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {b.status === 'confirmed' && (
                          <button onClick={() => updateStatus(b.id, 'completed')}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100">
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// NEW BOOKING FORM
// ============================================
const NewBookingForm = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    activity_id: '', activity_name: '', guest_name: '', guest_phone: '', guest_email: '',
    booking_date: '', booking_time: '', num_guests: 1, pickup_location: '', additional_notes: '',
    stay_type: '', selling_price: 0, selling_currency: 'EUR'
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAffiliateAxios().get(`${API}/affiliate/activities`);
        setActivities(res.data);
      } catch { toast.error('Failed to load activities'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleActivityChange = (activityId) => {
    const act = activities.find(a => a.id === activityId);
    if (act) {
      setForm(f => ({
        ...f, activity_id: act.id, activity_name: act.name,
        selling_price: act.b2c_price || 0, selling_currency: act.b2c_currency || 'EUR'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await getAffiliateAxios().post(`${API}/affiliate/bookings`, form);
      toast.success('Booking created!');
      const isAffDomain = window.location.hostname.includes('activities.sefertunisia') || window.location.hostname.includes('activities.sefer');
      navigate(isAffDomain ? '/bookings' : '/affiliate/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto" data-testid="new-booking-form">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">New Booking</h2>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Activity *</label>
          <select required value={form.activity_id} onChange={e => handleActivityChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="booking-activity-select">
            <option value="">Select activity</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.name} ({a.b2b_price} {a.b2b_currency})</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
            <input required value={form.guest_name} onChange={e => setForm(f => ({...f, guest_name: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="booking-guest-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone / WhatsApp *</label>
            <input required value={form.guest_phone} onChange={e => setForm(f => ({...f, guest_phone: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="booking-guest-phone" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
          <input type="email" value={form.guest_email} onChange={e => setForm(f => ({...f, guest_email: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input type="date" required value={form.booking_date} onChange={e => setForm(f => ({...f, booking_date: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="booking-date" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
            <input type="time" value={form.booking_time} onChange={e => setForm(f => ({...f, booking_time: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guests *</label>
            <input type="number" min="1" required value={form.num_guests} onChange={e => setForm(f => ({...f, num_guests: parseInt(e.target.value) || 1}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="booking-num-guests" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Stay Type</label>
          <select value={form.stay_type} onChange={e => setForm(f => ({...f, stay_type: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2">
            <option value="">N/A</option>
            <option value="hostel">Hostel Guest</option>
            <option value="hotel">Hotel Guest</option>
            <option value="airbnb">Airbnb Guest</option>
            <option value="cruise">Cruise Guest</option>
            <option value="local">Local</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price</label>
            <input type="number" step="0.01" min="0" value={form.selling_price}
              onChange={e => setForm(f => ({...f, selling_price: parseFloat(e.target.value) || 0}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="booking-selling-price" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select value={form.selling_currency} onChange={e => setForm(f => ({...f, selling_currency: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2">
              <option value="EUR">EUR</option><option value="USD">USD</option><option value="DT">DT</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pickup / Meeting Location</label>
          <input value={form.pickup_location} onChange={e => setForm(f => ({...f, pickup_location: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea value={form.additional_notes} onChange={e => setForm(f => ({...f, additional_notes: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={3} />
        </div>

        <button type="submit" disabled={submitting}
          className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
          data-testid="submit-booking-button">
          {submitting ? 'Creating...' : 'Create Booking'}
        </button>
      </form>
    </div>
  );
};

// ============================================
// MAIN COMPONENT (Route-based sub-pages)
// ============================================
const AffiliateDashboard = ({ section = 'dashboard' }) => {
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('affiliate_token');
      const isAffDomain = window.location.hostname.includes('activities.sefertunisia') || window.location.hostname.includes('activities.sefer');
      const loginPath = isAffDomain ? '/login' : '/affiliate/login';
      if (!token) { navigate(loginPath); return; }
      try {
        const res = await getAffiliateAxios().get(`${API}/affiliate/auth/me`);
        setAffiliate(res.data);
        localStorage.setItem('affiliate_data', JSON.stringify(res.data));
      } catch {
        localStorage.removeItem('affiliate_token');
        navigate(loginPath);
      } finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-xl">Loading...</div></div>;
  if (!affiliate) return null;

  const renderSection = () => {
    switch (section) {
      case 'activities': return <ActivitiesList />;
      case 'bookings': return <BookingsList />;
      case 'new-booking': return <NewBookingForm />;
      default: return <StatsDashboard affiliate={affiliate} />;
    }
  };

  return <AffiliateLayout affiliate={affiliate}>{renderSection()}</AffiliateLayout>;
};

export default AffiliateDashboard;
